// react
import React, { useState } from 'react';

// mui
import {
  Box,
  Button,
  ButtonBase,
  Card,
  Stack,
  Typography,
} from '@mui/material';

// components
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { BottomSheetDrawer } from '@/pwa/organisms/BottomSheetDrawer/BottomSheetDrawer';
import { Icon } from '@/components/atoms/Icon/Icon';
import { Icons } from '@/components/atoms/Icon';

// local
import { PoaPricing } from './PoaPricing';

// Home screen ---------------------------------------------------------------

/**
 * A tappable option on the home screen: leading icon, title and supporting
 * line, the whole card being the target. `isFeatured` uses the lavender
 * "featured" Card variant to promote the primary choice.
 */
const OptionCard = ({
  icon,
  title,
  description,
  onClick,
  isFeatured,
}: {
  icon: Icons;
  title: string;
  description: string;
  onClick: () => void;
  isFeatured?: boolean;
}) => (
  <Card
    component={ButtonBase}
    variant={isFeatured ? 'featured' : 'padded'}
    onClick={onClick}
  >
    <Stack
      direction='row'
      sx={{
        alignItems: 'center',
        gap: 3,
        width: '100%',
        textAlign: 'left',
      }}
    >
      <Icon icon={icon} fontSize={24} />
      <Stack
        spacing={0.5}
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography
          variant='h4'
          sx={{
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
        <Typography
          variant='body1'
          sx={{
            color: 'text.secondary',
          }}
        >
          {description}
        </Typography>
      </Stack>
    </Stack>
  </Card>
);

/** Footer shared by the two explainer drawers: apply, or talk to the team. */
const DrawerActions = ({
  onReadyToApply,
  onTalkToTeam,
}: {
  onReadyToApply: () => void;
  onTalkToTeam: () => void;
}) => (
  <Stack spacing={2}>
    <Button variant='contained' size='large' fullWidth onClick={onReadyToApply}>
      I’m ready to apply
    </Button>
    <Button variant='outlined' size='large' fullWidth onClick={onTalkToTeam}>
      Talk to our team
    </Button>
  </Stack>
);

interface HomeProps {
  /** Start the application — routes to the pricing calculator. */
  onApply: () => void;
  /** Book a call with the care team for guidance first. */
  onBookCall: () => void;
  /** Proceed to apply from a drawer. */
  onReadyToApply: () => void;
  /** Book a call with the care team from a drawer. */
  onTalkToTeam: () => void;
}

/**
 * PoaHome is the Power of Attorney entry point. It lets users learn before they
 * commit (two explainer drawers) or start applying. A calm starting point, not
 * a sales funnel — a user might land here to learn, to think, then to apply.
 */
const PoaHome = ({
  onApply,
  onBookCall,
  onReadyToApply,
  onTalkToTeam,
}: HomeProps) => {
  const [openDrawer, setOpenDrawer] = useState<'what' | 'why' | null>(null);
  const closeDrawer = () => setOpenDrawer(null);

  // Close the drawer before booking a call so it isn't left open behind it.
  const handleTalkToTeam = () => {
    closeDrawer();
    onTalkToTeam();
  };

  return (
    <PageTemplate
      maxWidth='sm'
      headerSlot={
        <Box
          sx={{
            p: 2,
          }}
        >
          <PageHeader
            variant='h2'
            title='How can we help you today?'
            description='Whether you’re learning or ready to apply, we’re here to help.'
          />
        </Box>
      }
      contentSlot={
        <Stack spacing={2}>
          <OptionCard
            icon='book-open'
            title='What is power of attorney?'
            description='A quick overview of LPAs and how they work.'
            onClick={() => setOpenDrawer('what')}
          />
          <OptionCard
            icon='shield-check'
            title='Why set up power of attorney?'
            description='Why it matters and what happens without one.'
            onClick={() => setOpenDrawer('why')}
          />
          <OptionCard
            icon='user-headset'
            title='I’d like some guidance first'
            description='Book a call with our care team to talk through your options.'
            onClick={onBookCall}
          />
          <OptionCard
            icon='file-lines'
            title='Apply for power of attorney'
            description='Start your application with expert support.'
            isFeatured
            onClick={onApply}
          />

          <BottomSheetDrawer
            isOpen={openDrawer === 'what'}
            onClose={closeDrawer}
            title='What is Power of Attorney'
            footer={
              <DrawerActions
                onReadyToApply={onReadyToApply}
                onTalkToTeam={handleTalkToTeam}
              />
            }
          >
            <Stack spacing={2}>
              <Typography variant='body1'>
                A Lasting Power of Attorney (LPA) is a legal document that lets
                someone you trust make decisions on your behalf if you’re unable
                to.
              </Typography>
              <Typography variant='body1'>There are 2 types:</Typography>
              <Box
                component='ol'
                sx={{
                  m: 0,
                  pl: 3,
                }}
              >
                <Box
                  component='li'
                  sx={{
                    mb: 2,
                  }}
                >
                  <Typography
                    variant='h4'
                    sx={{
                      color: 'text.primary',
                    }}
                  >
                    Property and financial affairs
                  </Typography>
                  <Typography variant='body1'>
                    Covers things like paying bills, managing a bank account, or
                    selling a home. This can be used as soon as it’s registered,
                    even while the person still has capacity.
                  </Typography>
                </Box>
                <Box
                  component='li'
                  sx={{
                    mb: 2,
                  }}
                >
                  <Typography
                    variant='h4'
                    sx={{
                      color: 'text.primary',
                    }}
                  >
                    Health and welfare
                  </Typography>
                  <Typography variant='body1'>
                    Covers decisions about medical treatment, daily care, and
                    where someone lives. This can only be used after the person
                    has lost the capacity to decide for themselves.
                  </Typography>
                </Box>
              </Box>
              <Typography variant='body1'>
                You can set up one or both. Most people set up both.
              </Typography>
            </Stack>
          </BottomSheetDrawer>

          <BottomSheetDrawer
            isOpen={openDrawer === 'why'}
            onClose={closeDrawer}
            title='Why set up Power of Attorney'
            footer={
              <DrawerActions
                onReadyToApply={onReadyToApply}
                onTalkToTeam={handleTalkToTeam}
              />
            }
          >
            <Stack spacing={2}>
              <Typography variant='body1'>
                If someone loses the ability to make decisions and there’s no
                LPA in place, no one, not even their spouse or children, can
                legally act on their behalf.
              </Typography>
              <Typography variant='body1'>
                The alternative is applying to the Court of Protection, which
                can take over a year, costs significantly more, and adds stress
                at an already difficult time.
              </Typography>
              <Typography variant='body1'>
                Setting up an LPA while the person still has capacity means your
                family can step in quickly and with less friction when the time
                comes. It’s one of the most practical things you can do to
                prepare.
              </Typography>
              <Typography variant='body1'>
                Around{' '}
                <Typography
                  component='span'
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  15% of self-filed LPAs are returned due to errors
                </Typography>
                , which is why many families choose to use a legal partner to
                handle the paperwork.
              </Typography>
            </Stack>
          </BottomSheetDrawer>
        </Stack>
      }
    />
  );
};

// Page ----------------------------------------------------------------------

export interface PoaProps {
  /** Which screen to render — both routes mount this one page. */
  screen: 'home' | 'pricing';
  /** Back always returns home (the dashboard). */
  onBack: () => void;
  /** Home: start the application (→ pricing). */
  onApply: () => void;
  /** Home + pricing: book a call with the care team. */
  onBookCall: () => void;
  /** Home drawers: proceed to apply. */
  onReadyToApply: () => void;
  /** Home drawers: book a call with the care team. */
  onTalkToTeam: () => void;
  /** Pricing: whether the user's first online legal review is free. */
  isFirstReviewFree: boolean;
  /** Pricing: open the Zenco partner site. */
  onContinueToZenco: () => void;
  /** Pricing: open the official GOV.UK LPA service (independent path). */
  onApplyGovUk: () => void;
}

/**
 * Poa is the single Power of Attorney page, mounted at both POA routes with a
 * `screen` prop: the home entry point (learn / book a call / apply) and the
 * pricing calculator (with its in-place Zenco handoff). The shared modal shell
 * and the always-home back button live here; each screen owns its own content.
 */
export const Poa = ({
  screen,
  onBack,
  onApply,
  onBookCall,
  onReadyToApply,
  onTalkToTeam,
  isFirstReviewFree,
  onContinueToZenco,
  onApplyGovUk,
}: PoaProps) => (
  <ModalPageTemplate onBack={onBack}>
    {screen === 'home' ? (
      <PoaHome
        onApply={onApply}
        onBookCall={onBookCall}
        onReadyToApply={onReadyToApply}
        onTalkToTeam={onTalkToTeam}
      />
    ) : (
      <PoaPricing
        isFirstReviewFree={isFirstReviewFree}
        onContinueToZenco={onContinueToZenco}
        onBookCall={onBookCall}
        onApplyGovUk={onApplyGovUk}
      />
    )}
  </ModalPageTemplate>
);

Poa.displayName = 'Poa';
export default Poa;
